import { useCallback, useEffect, useRef, useState } from 'react';
import { t } from '@lingui/macro';
import { useApolloClient } from '@apollo/client';
import { useNavigate } from 'react-router-dom';

import AddAssetModal from 'pages/Management/Account/Depots/AddAssetModal';
import ResetDepotAssetsModal from 'pages/Management/Account/Depots/ResetDepotAssetsModal';
import BulkCreateContactsModal from 'pages/Management/Contact/BulkCreateContactsModal';
import CreateContactModal from 'pages/Management/Contact/CreateContactModal';
import BulkCreateDepotModal from 'pages/Management/Depots/BulkCreateDepotModal';
import CreateDepotModal from 'pages/Management/Depots/CreateDepotModal';
import BulkAddAssetModal from 'pages/Management/Fleets/BulkAddAssetModal';
import BulkAddAssetProvider, {
  useBulkAddAssetContext,
} from 'pages/Management/Fleets/BulkAddAssetModal/context/BulkAddAssetContext';
import CreateAccountModal from 'pages/Management/Fleets/CreateAccountModal';
import SectionedMenuButton from 'pages/Management/Team/components/SectionedMenuButton';
import { TABLE_OVERLAY_MENU_Z_INDEX } from 'pages/Management/constants';
import {
  hasNoPlatformAccounts,
  noPlatformAccountsReason,
} from 'pages/Teams/Cells/NameCell/noPlatformAccounts';
import ROUTE_NAMES from 'setup/Routing/routeNames';
import { buildTeamPath } from 'utils/teamFromUrl';
import useFleetAdminPermissions from 'utils/User/useFleetAdminPermissions';

import type { ManagedTeamsType } from '../..';

type RowModal =
  | 'createAccount'
  | 'createDepot'
  | 'createDepotsFromFile'
  | 'addAssetsByVin'
  | 'replaceAssetList'
  | 'createContact'
  | 'createContactsFromFile';

type AccountCapabilityKey =
  | 'canManageAssets'
  | 'canManageContacts'
  | 'canManageDepots';

interface CellProps {
  rowData: ManagedTeamsType;
}

function TeamActions({ rowData }: CellProps) {
  const navigate = useNavigate();
  const permissions = useFleetAdminPermissions();
  const { isModalOpen: bulkAddOpen, openModal: openBulkAdd } =
    useBulkAddAssetContext();
  const [modal, setModal] = useState<RowModal | null>(null);

  const accounts = rowData.platformAccounts ?? [];
  const noAccounts = hasNoPlatformAccounts(rowData.platformAccounts);
  // One-account flows open here only when the team has a single account.
  // Replace Asset List and Create Contact have no account picker, and the
  // pickers of Create Depot and Add Assets by VIN list accounts from every
  // team, so a team with several accounts goes to its Accounts tab instead.
  const singleAccountId = accounts.length === 1 ? accounts[0].id : undefined;
  const openTeamAccounts = () =>
    navigate(ROUTE_NAMES.forAdminManagementTeamTab(rowData.id, 'accounts'));

  // Allowed when the user may do it on one of the team's accounts. An account
  // missing from the user's managed accounts (e.g. a superuser's) has no
  // permissions there, so fall back to the user's capability on any account.
  const isAllowed = (key: AccountCapabilityKey) => {
    const known = accounts.filter(({ id }) => permissions.forAccount(id));
    if (known.length === 0) return permissions.anyAccount(key).allowed;
    return known.some(
      ({ id }) => permissions.accountCapability(id, key).allowed,
    );
  };
  const canManageDepots = !noAccounts && isAllowed('canManageDepots');
  const canManageAssets = !noAccounts && isAllowed('canManageAssets');
  const canManageContacts = !noAccounts && isAllowed('canManageContacts');
  const canCreateAccount = permissions.capability(
    rowData.oemSegment?.id,
    'canCreateAccount',
  ).allowed;

  // The row flows' modals refresh only their own pages' queries, so the
  // Teams list refetches its counts and platformAccounts once one closes.
  const apolloClient = useApolloClient();
  const refetchTeams = useCallback(() => {
    apolloClient.refetchQueries({ include: ['ManagedSentinelTeams'] });
  }, [apolloClient]);
  const close = () => {
    setModal(null);
    refetchTeams();
  };

  // BulkAddAssetProvider closes its own modal, so watch for the close rather
  // than hooking a callback; refetch once the upload modal goes away.
  const wasBulkAddOpen = useRef(bulkAddOpen);
  useEffect(() => {
    if (wasBulkAddOpen.current && !bulkAddOpen) refetchTeams();
    wasBulkAddOpen.current = bulkAddOpen;
  }, [bulkAddOpen, refetchTeams]);

  return (
    <>
      <SectionedMenuButton
        aria-label={t`Team Actions`}
        zIndex={TABLE_OVERLAY_MENU_Z_INDEX}
        sections={[
          {
            id: 'pssm',
            items: [
              {
                id: 'open-in-pssm-fleet',
                text: t`Open in PSSM Fleet`,
                disabled: noAccounts || !!rowData.scheduledForDeletionAt,
                onSelect: () =>
                  window.open(
                    buildTeamPath(rowData.teamKey, '/'),
                    '_blank',
                    'noopener, noreferrer',
                  ),
              },
            ],
            // KDS Menu items take only string text, so the tooltip the old
            // team name link showed is help text under the disabled item.
            helpText: noAccounts ? noPlatformAccountsReason() : undefined,
          },
          {
            id: 'accounts',
            title: t`Accounts`,
            items: [
              {
                id: 'create-account',
                text: t`Create Account`,
                disabled: !canCreateAccount,
                onSelect: () => setModal('createAccount'),
              },
            ],
          },
          {
            id: 'depots',
            title: t`Depots`,
            items: [
              {
                id: 'create-depot',
                text: t`Create Depot`,
                disabled: !canManageDepots,
                onSelect: () =>
                  singleAccountId
                    ? setModal('createDepot')
                    : openTeamAccounts(),
              },
              {
                id: 'create-depots-from-file',
                text: t`Create Depots from File`,
                disabled: !canManageDepots,
                onSelect: () => setModal('createDepotsFromFile'),
              },
            ],
          },
          {
            id: 'assets',
            title: t`Assets`,
            items: [
              {
                id: 'add-assets-by-vin',
                text: t`Add Assets by VIN`,
                disabled: !canManageAssets,
                onSelect: () =>
                  singleAccountId
                    ? setModal('addAssetsByVin')
                    : openTeamAccounts(),
              },
              {
                id: 'add-assets-from-file',
                text: t`Add Assets from File`,
                disabled: !canManageAssets,
                onSelect: openBulkAdd,
              },
              {
                id: 'replace-asset-list',
                text: t`Replace Asset List`,
                disabled: !canManageAssets,
                onSelect: () =>
                  singleAccountId
                    ? setModal('replaceAssetList')
                    : openTeamAccounts(),
              },
            ],
            helpText: t`Upload a CSV file with the complete list of assets for an account. The file becomes the account's asset inventory: assets not in it are released.`,
          },
          {
            id: 'contacts',
            title: t`Contacts`,
            items: [
              {
                id: 'create-contact',
                text: t`Create Contact`,
                disabled: !canManageContacts,
                onSelect: () =>
                  singleAccountId
                    ? setModal('createContact')
                    : openTeamAccounts(),
              },
              {
                id: 'create-contacts-from-file',
                text: t`Create Contacts from File`,
                disabled: !canManageContacts,
                onSelect: () => setModal('createContactsFromFile'),
              },
            ],
          },
        ]}
      />
      {modal === 'createAccount' && (
        <CreateAccountModal
          onClose={close}
          sentinelTeamId={rowData.id}
          oemSegmentId={rowData.oemSegment?.id}
        />
      )}
      {modal === 'createDepot' && (
        <CreateDepotModal
          visible
          handleClose={close}
          accountId={singleAccountId}
        />
      )}
      {modal === 'createDepotsFromFile' && (
        <BulkCreateDepotModal onCancel={close} />
      )}
      {modal === 'addAssetsByVin' && (
        <AddAssetModal
          accountId={singleAccountId}
          onCancel={close}
          onOpenBulkAdd={() => {
            close();
            openBulkAdd();
          }}
        />
      )}
      {bulkAddOpen && <BulkAddAssetModal />}
      {modal === 'replaceAssetList' && singleAccountId && (
        <ResetDepotAssetsModal
          accountId={singleAccountId}
          target={{ scope: 'account' }}
          onClose={close}
        />
      )}
      {modal === 'createContact' && (
        <CreateContactModal onClose={close} accountId={singleAccountId} />
      )}
      {modal === 'createContactsFromFile' && (
        <BulkCreateContactsModal onCancel={close} />
      )}
    </>
  );
}

/**
 * The "⋯" menu of an All Teams row (Figma 302:59809), shown with
 * FEATURE_FLAGS.PD_5874_FLEET_ASSET_MANAGEMENT on. Each flow opens here with
 * the row's team (and its account, when it has only one) preselected.
 */
export default function TeamActionsCell({ rowData }: CellProps) {
  return (
    <BulkAddAssetProvider>
      <TeamActions rowData={rowData} />
    </BulkAddAssetProvider>
  );
}
